import { gql } from '@apollo/client';

/**
 * 工作台写操作。
 *
 * 全部返回 `Boolean`（是否发生变更）而非更新后的对象 —— 上游写接口只回 Boolean，
 * 故调用后需 refetch 列表刷新，不能就地改单条。
 *
 * **返回 false 不是错误**：上游对重复提交做状态守卫（已完结再点完结返回 false），
 * 调用方应提示「该项已处理」而非报错。
 */

export const COMPLETE_WORKBENCH_TASK = gql`
  mutation CompleteWorkbenchTask(
    $warehouseId: Long!
    $taskId: Long!
    $photoS3Keys: [String!]
    $comment: String
    $attachType: String
  ) {
    completeWorkbenchTask(
      warehouseId: $warehouseId
      taskId: $taskId
      photoS3Keys: $photoS3Keys
      comment: $comment
      attachType: $attachType
    )
  }
`;

export const MARK_WORKBENCH_ALERT_HANDLED = gql`
  mutation MarkWorkbenchAlertHandled(
    $warehouseId: Long!
    $alertId: Long!
    $actionCode: String
    $actionTaken: String
  ) {
    markWorkbenchAlertHandled(
      warehouseId: $warehouseId
      alertId: $alertId
      actionCode: $actionCode
      actionTaken: $actionTaken
    )
  }
`;

/**
 * 片区派发任务给子仓。返回新建任务 id（失败为 null），是本文件唯一不返回 Boolean 的写操作。
 *
 * `dueAt` 是**绝对时刻**（ISO-8601 UTC）。设计稿原本是「次日开始 / 次日结束 / 今日结束」三个
 * 相对选项，但系统内没有班次时间定义，换算口径无从确定，故改为让派发人直接选定时刻。
 */
export const CREATE_HUB_TASK = gql`
  mutation CreateHubTask(
    $hubId: Long!
    $warehouseId: Long!
    $title: String!
    $detail: String
    $priority: String
    $requirePhoto: Boolean
    $dueAt: String!
  ) {
    createHubTask(
      hubId: $hubId
      warehouseId: $warehouseId
      title: $title
      detail: $detail
      priority: $priority
      requirePhoto: $requirePhoto
      dueAt: $dueAt
    )
  }
`;

/**
 * 约谈 DSP：把告警发到该 DSP 的 Lark 群。
 *
 * `dspId` 可缺省 —— 实时告警是 DSP 维度卡，上游取任务的对象维度即可；
 * **周期复盘是仓维度卡，必须显式指定**，否则上游无从判断发给谁。
 */
export const NOTIFY_WORKBENCH_DSP_BY_LARK = gql`
  mutation NotifyWorkbenchDspByLark(
    $warehouseId: Long!
    $alertId: Long!
    $dspId: Long
    $comment: String
    $language: String
  ) {
    notifyWorkbenchDspByLark(
      warehouseId: $warehouseId
      alertId: $alertId
      dspId: $dspId
      comment: $comment
      language: $language
    )
  }
`;

/**
 * 保存日报草稿。`reportId` 取自 `workbenchShiftReport` 返回体 —— 上游按日报主键定位；
 * 当日日报尚未生成时该字段可为空，由上游按仓 + 业务日兜底新建。
 */
export const SAVE_WORKBENCH_DAILY_REPORT_DRAFT = gql`
  mutation SaveWorkbenchDailyReportDraft($warehouseId: Long!, $reportId: Long, $remark: String) {
    saveWorkbenchDailyReportDraft(
      warehouseId: $warehouseId
      reportId: $reportId
      remark: $remark
    )
  }
`;

/**
 * 改日报里某条风险的留言。
 *
 * 日报里的实时告警风险是处理当刻写进日报草稿的快照，走 `commentTask` 改不到它（TITA-1980）。
 * 后端按 `reportId` 定位草稿整份回写；命中不到（周期复盘那批风险）时自动回落到 `commentTask`。
 */
export const SAVE_WORKBENCH_DAILY_REPORT_RISK_COMMENT = gql`
  mutation SaveWorkbenchDailyReportRiskComment(
    $warehouseId: Long!
    $reportId: Long
    $alertId: Long!
    $content: String!
  ) {
    saveWorkbenchDailyReportRiskComment(
      warehouseId: $warehouseId
      reportId: $reportId
      alertId: $alertId
      content: $content
    )
  }
`;
